import React from 'react';
import PropTypes from 'prop-types';
import { Table, Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { GLOBAL_TABLE_SIZE } from 'global_config/constant-global';
import LockingService from 'shared_components/LockingService';

import ExpandedTable from '../expanded_table';
import './main_table.css';

const getColumns = () => [
	{
		title: 'REQUEST ID',
		dataIndex: 'requestID',
		width: 120,
		sorter: (a, b) => a.requestID - b.requestID,
	},
	{
		title: 'REQUEST DATE',
		dataIndex: 'requestDateTime',
		width: 150,
		render: text => text.replace(/\//g, '-'),
		sorter: (a, b) => {
			const parse = str => {
				if (!str) return 0;
				const [date, time] = str.split(' ');
				if (!date || !time) return 0;
				const [month, day, year] = date.split('-').map(Number);
				const [hour, minute, second] = time.split(':').map(Number);
				return new Date(year, month - 1, day, hour, minute, second).getTime();
			};
			return parse(a.requestDateTime) - parse(b.requestDateTime);
		},
	},
	{
		title: 'PATIENT ID',
		dataIndex: 'patientID',
		width: 100,
		sorter: (a, b) => a.patientID - b.patientID,
	},
	{
		title: 'LAST NAME',
		dataIndex: 'lastName',
		width: 150,
		sorter: (a, b) => a.lastName.localeCompare(b.lastName),
	},
	{
		title: 'FIRST NAME',
		dataIndex: 'givenName',
		width: 150,
		sorter: (a, b) => a.givenName.localeCompare(b.givenName),
	},
	{
		title: 'MIDDLE NAME',
		dataIndex: 'middleName',
		width: 150,
		sorter: (a, b) => a.middleName.localeCompare(b.middleName),
	},
	{
		title: 'BIRTHDATE',
		dataIndex: 'dateOfBirth',
		width: 100,
		sorter: (a, b) => a.dateOfBirth - b.dateOfBirth,
	},
	{
		title: 'GENDER',
		dataIndex: 'sex',
		width: 80,
		sorter: (a, b) => a.sex.localeCompare(b.sex),
	},
	{
		title: 'ADDRESS',
		render: (text, record) =>
			`${record.address || ''} ${record.townName || ''} ${record.cityMunicipalityName ||
				''} ${record.provinceName || ''}`.toUpperCase(),
		sorter: (a, b) => a.address.localeCompare(b.address),
		width: 400,
	},
];

class MainTable extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			expandedRequestIDs: [],
			lockedRequestIDs: [],
			expandedRowKeys: [],
			currentPage: 1
		};

		// Initialize LockingService
		this.lockingService = new LockingService('RESULT', this.handleLockUpdate);
		// Bind page change handler
		this.handlePageChange = this.handlePageChange.bind(this);
	}
	handlePageChange(page, pageSize) {
		// Update state or call a prop as needed
		this.setState({ currentPage: page });
		if (this.props.onPageChange) {
			this.props.onPageChange(page, pageSize);
		}
	}

	componentDidMount() {
		// Subscribe to WebSocket using LockingService
		this.lockingService.subscribe();
	}

	componentDidUpdate(prevProps) {
		// Reset expanded rows when new s come in
		if (prevProps.labResults !== this.props.labResults) {
			this.setState({
				expandedRowKeys: [],
				expandedRequestIDs: []
			});
		}
	}

	componentWillUnmount() {
		// Unsubscribe from WebSocket
		this.lockingService.unsubscribe();
	}

	// Callback when locks are updated from LockingService
	handleLockUpdate = (lockedIDs) => {
		this.setState({ lockedRequestIDs: lockedIDs });
	};

	expandedRowRender = record => {
		const { onClickTableRow, onClickPrint } = this.props;
		return (
			<ExpandedTable
				expandedData={record}
				onClickTableRow={onClickTableRow}
				onClickPrint={onClickPrint}
			/>
		);
	};

	handleExpand = (expanded, record) => {
		const requestIdNum = parseInt(record.requestID, 10);

		// Use LockingService to check if locked
		if (expanded && this.lockingService.isResourceLocked(record.requestID)) {
			return false;
		}

		this.setState(prevState => {
			let updatedRequestIDs;
			let updatedExpandedRowKeys;

			if (expanded) {
				if (!prevState.expandedRequestIDs.includes(record.requestID)) {
					updatedRequestIDs = [...prevState.expandedRequestIDs, record.requestID];
					updatedExpandedRowKeys = [...prevState.expandedRowKeys, record.requestID];
				} else {
					updatedRequestIDs = prevState.expandedRequestIDs;
					updatedExpandedRowKeys = prevState.expandedRowKeys;
				}
			} else {
				updatedRequestIDs = prevState.expandedRequestIDs.filter(id => id !== record.requestID);
				updatedExpandedRowKeys = prevState.expandedRowKeys.filter(id => id !== record.requestID);
			}

			// Update session using LockingService
			LockingService.updateSessionLocks(updatedRequestIDs);

			return {
				expandedRequestIDs: updatedRequestIDs,
				expandedRowKeys: updatedExpandedRowKeys
			};
		});
	};

	getRowClassName = (record) => {
		const requestIdNum = parseInt(record.requestID, 10);
		const { lockedRequestIDs } = this.state;
		return lockedRequestIDs.includes(requestIdNum) ? 'locked-row' : '';
	};

	expandIcon = ({ expanded, onExpand, record }) => {
		const requestIdNum = parseInt(record.requestID, 10);
		const { lockedRequestIDs } = this.state;
		const isLocked = lockedRequestIDs.includes(requestIdNum);

		if (isLocked) {
			return (
				<Tooltip title="This record is being edited by another user">
					<LockOutlined style={{ color: '#ff4d4f', cursor: 'not-allowed' }} />
				</Tooltip>
			);
		}

		// Return default Ant Design expand icon for unlocked rows
		const className = expanded
			? 'ant-table-row-expand-icon ant-table-row-expand-icon-expanded'
			: 'ant-table-row-expand-icon ant-table-row-expand-icon-collapsed';

		return (
			<button
				type="button"
				onClick={(e) => {
					onExpand(record, e);
				}}
				className={className}
				aria-label={expanded ? 'Collapse row' : 'Expand row'}
			/>
		);
	};

	render() {
		const { labResults, isLoading, pageSize } = this.props;
		const { expandedRowKeys } = this.state;

		return (
			<div className="test-results-table-container">
				<Table
					className="test-results-table"
					columns={getColumns()}
				pagination={{
					pageSize,
					showSizeChanger: false,
					onChange: this.handlePageChange,
					current: this.state.currentPage
				}}
					expandable={{
						expandedRowKeys: expandedRowKeys,
						onExpand: this.handleExpand,
						expandIcon: this.expandIcon,
						expandedRowRender: this.expandedRowRender
					}}
					dataSource={labResults}
					rowKey={record => record.requestID}
					size={GLOBAL_TABLE_SIZE}
					loading={isLoading}
					scroll={{ x: 1500 }}
					rowClassName={this.getRowClassName}
				/>
			</div>
		);
	}
}

MainTable.propTypes = {
	isLoading: PropTypes.bool.isRequired,
	labResults: PropTypes.array.isRequired,
	pageSize: PropTypes.number.isRequired,
	onClickTableRow: PropTypes.func.isRequired,
	onClickPrint: PropTypes.func.isRequired,
};

export default MainTable;
